// 190 mm at 96 dpi: the width a report is laid out at when printed (A4 less 10 mm each side)
export const PRINT_WIDTH_PX = Math.round((190 / 25.4) * 96);
export const PDF_MESSAGE = "audit-report-pdf";
// reports made side by side
const PARALLEL = 2;
const REPORT_TIMEOUT_MS = 180000;

const clean = (text) =>
  String(text ?? "")
    .replace(/[\\/:*?"<>|#%&{}$!'@+`=]+/g, " ")
    .replace(/\s+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80);

// "Phoenix_Mall_PNQ01_2026-10-05_1234.pdf"; the report id keeps two visits to a store apart
export const reportFileName = (row) =>
  [clean(row.store_name) || "Report", clean(row.store_code), clean(row.audit_date), row.audit_store_id]
    .filter(Boolean)
    .join("_") + ".pdf";

const saveFile = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// Opens one report out of sight; the report page makes its own PDF (?pdf=1) and posts it back.
const makeReportPdf = (auditStoreId) =>
  new Promise((resolve, reject) => {
    const frame = document.createElement("iframe");
    frame.setAttribute("aria-hidden", "true");
    frame.title = "report-pdf";
    Object.assign(frame.style, {
      position: "fixed",
      top: "0",
      left: "0",
      width: `${PRINT_WIDTH_PX}px`,
      height: "1000px",
      border: "0",
      opacity: "0",
      pointerEvents: "none",
      zIndex: "-1",
    });
    let timer;
    const done = () => {
      clearTimeout(timer);
      window.removeEventListener("message", onMessage);
      frame.remove();
    };
    const onMessage = (event) => {
      if (event.origin !== window.location.origin || event.source !== frame.contentWindow) return;
      const { type, blob, error } = event.data || {};
      if (type !== PDF_MESSAGE) return;
      done();
      if (blob) resolve(blob);
      else reject(new Error(error || "The report could not be made"));
    };
    timer = setTimeout(() => {
      done();
      reject(new Error("The report took too long to load"));
    }, REPORT_TIMEOUT_MS);
    window.addEventListener("message", onMessage);
    frame.src = `/auditReport/${auditStoreId}?pdf=1`;
    document.body.appendChild(frame);
  });

// Every report in `rows` as its own PDF, in one ZIP. Returns the reports that failed.
export const downloadReportPdfs = async (rows, { zipName, onProgress, isCancelled }) => {
  const { default: JSZip } = await import("jszip");
  const zip = new JSZip();
  const failed = [];
  const names = new Set();
  let next = 0;
  let finished = 0;

  const worker = async () => {
    while (next < rows.length && !isCancelled?.()) {
      const row = rows[next];
      next += 1;
      try {
        // a photo or the network can fail for a moment: one more try
        const blob = await makeReportPdf(row.audit_store_id).catch((error) =>
          isCancelled?.() ? Promise.reject(error) : makeReportPdf(row.audit_store_id),
        );
        let name = reportFileName(row);
        while (names.has(name)) name = name.replace(/\.pdf$/, "_1.pdf");
        names.add(name);
        zip.file(name, blob);
      } catch (error) {
        failed.push({ row, reason: error.message });
      }
      finished += 1;
      onProgress?.({ done: finished, total: rows.length, failed: failed.length });
    }
  };
  await Promise.all(Array.from({ length: Math.min(PARALLEL, rows.length) }, worker));
  if (isCancelled?.() || names.size === 0) return { failed, saved: 0 };

  if (failed.length) {
    zip.file(
      "Reports_not_included.txt",
      failed.map(({ row, reason }) => `${row.store_name} (${row.store_code ?? ""}), ${row.audit_date}: ${reason}`).join("\n"),
    );
  }
  // the PDFs are photos already compressed: storing them is as small and much quicker
  saveFile(await zip.generateAsync({ type: "blob", compression: "STORE" }), zipName);
  return { failed, saved: names.size };
};

// "Email PDF": the report's PDF, made here, emailed by the server to the addresses given (with a note).
// onStep("making") while the PDF is made, then onStep("sending").
export const emailReportPdf = async (row, emails, note = "", onStep) => {
  const { base_url } = await import("../apiConfig");
  const port = base_url.port ? `:${base_url.port}` : "";
  onStep?.("making");
  const pdf = await makeReportPdf(row.audit_store_id);
  onStep?.("sending");
  const form = new FormData();
  form.append("pdf", new File([pdf], reportFileName(row), { type: "application/pdf" }));
  form.append("emails", emails.join(","));
  form.append("report_id", String(row.audit_store_id));
  form.append("note", note);
  const response = await fetch(`${base_url.protocol}://${base_url.hostname}${port}${base_url.api_base_path}client/email_pdf`, {
    method: "POST",
    headers: { Authorization: `Token ${localStorage.getItem("authToken")}` },
    body: form,
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(result.error || result.detail || "The email could not be sent");
  return result;
};

// One report's PDF ("PDF Report" on the report page), made the same way as each PDF in the ZIP.
export const downloadReportPdf = async (row) => {
  saveFile(await makeReportPdf(row.audit_store_id), reportFileName(row));
};
